import { useSearchParams } from "react-router-dom";

import { EntityTabIds } from "src/entities/entityView/useEntityTabRefresh";
import {
  SidepaneIdParam,
  SidepaneTypeParam,
} from "src/entities/entityView/utils";
import { useEntityDisplayValues } from "src/entities/useEntityDisplayValues";
import { Environment } from "src/hooks/useEnvironment";
import {
  getUrlToCaseQueuePage,
  getUrlToEntityDetailPage,
} from "src/router/urls";

/**
 * Query params set when a case is opened from an entity view. They let the
 * case page point its "back" affordance at the originating entity instead of
 * defaulting to the case's queue.
 */
export const CASE_ORIGIN_SCHEMA_PARAM = "originSchema";
export const CASE_ORIGIN_ID_PARAM = "originId";

type UseCaseBackNavigationParams = {
  orgId: string;
  wsId: string;
  caseId: string;
  queueId: string | undefined;
  queueName: string | undefined;
  /** The case's own environment — authoritative over the URL's `env` param. */
  environment: Environment;
  searchParams: Record<string, string>;
};

type CaseBackNavigation = {
  /** Destination of the back arrow / action. */
  backLink: string;
  /** Lead-in label for the case page header title. */
  titleLabel: string;
  /** Full "Back to X" label for error / empty states. */
  backToLabel: string;
  isFromEntity: boolean;
};

/**
 * Resolves where the case page's "back" navigation should go. When the case
 * was opened from an entity (via {@link CASE_ORIGIN_SCHEMA_PARAM} /
 * {@link CASE_ORIGIN_ID_PARAM}), back points at that entity and is labelled
 * with the entity's primary-property value; otherwise it falls back to the
 * case's queue.
 */
export const useCaseBackNavigation = ({
  orgId,
  wsId,
  caseId,
  queueId,
  queueName,
  environment,
  searchParams,
}: UseCaseBackNavigationParams): CaseBackNavigation => {
  const [params] = useSearchParams();

  const originSchema = params.get(CASE_ORIGIN_SCHEMA_PARAM) ?? undefined;
  const originId = params.get(CASE_ORIGIN_ID_PARAM) ?? undefined;
  const isFromEntity = Boolean(originSchema && originId);

  const { displayMap } = useEntityDisplayValues({
    entityRefs:
      originSchema && originId
        ? [{ id: originId, entity_type: originSchema }]
        : [],
    environment,
    enabled: isFromEntity,
  });

  if (originSchema && originId) {
    const originLabel = displayMap[originId] || originSchema;
    return {
      backLink: getUrlToEntityDetailPage(
        orgId,
        wsId,
        originSchema,
        originId,
        EntityTabIds.Cases,
        environment,
        // Reopen the case side pane on the entity's Cases tab.
        { [SidepaneTypeParam]: "case", [SidepaneIdParam]: caseId },
      ),
      titleLabel: originLabel,
      backToLabel: `Back to ${originLabel}`,
      isFromEntity: true,
    };
  }

  return {
    backLink: getUrlToCaseQueuePage({
      orgId,
      wsId,
      queueId,
      environment,
      searchParams,
    }),
    titleLabel: queueName ? `${queueName} Queue` : "Queue",
    backToLabel: "Back to queues",
    isFromEntity: false,
  };
};
